import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import "./AppTimeOffRequest.css";
import { DC_COMPONENT_LABELS, EmployeeRecord, PaidTimeOffPolicy, TimeOffRequest, TimeOffRequestStatus, UpdateMessageContentContract } from "winslow-core";
import toast from "react-hot-toast";
import { toastFail } from "../../../config";
import DatePicker from "react-datepicker";
import { useNavigate, useParams } from "react-router-dom";
import { apiCreateTimeOffRequest, apiGetTimeOffRequest } from "../../../services/TimeOffRequestService";
import { apiGetPaidTimeOffPoliciesForWorkspace } from "../../../services/PaidTimeOffPolicyService";
import { getPTODataForEmployeeRecord, PTOData } from "./ptoUtils";
import { DCUIContext } from "../dynamic-components/DCComponentRouter";
import { apiGetEmployeeRecord } from "../../../services/EmployeeRecordService";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { AppChanges } from "../app-changes/AppChanges";
import { X } from "lucide-react";
import { Icon } from "../../../ds2/components";
import { GenericSelect } from "../../shared/GenericSelect";
import { apiUpdateMessageContent } from "../../../services/MessageService";

type AppTimeOffRequestProps = {
    dynamicContext: DCUIContext | undefined;
    balanceOnly?: boolean;
    disabled?: boolean;
    onNavigation?: (
        newObjectName: DC_COMPONENT_LABELS, 
        newLabel: string,
        data: any
    ) => void;
}

export const AppTimeOffRequest = ({ dynamicContext, balanceOnly = false, disabled = false, onNavigation }: AppTimeOffRequestProps) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const params = useParams();

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);

    const [employeeRecordId, setEmployeeRecordId] = useState<string | undefined>(undefined);
    const [employeeRecord, setEmployeeRecord] = useState<EmployeeRecord | undefined>(undefined)

    const [isEditing, setIsEditing] = useState(false);
    const [timeOffRequest, setTimeOffRequest] = useState<TimeOffRequest | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [policies, setPolicies] = useState<PaidTimeOffPolicy[]>([]);
    const [timeRequested, setTimeRequested] = useState<number>(0);
    const [ptoData, setPTOData] = useState<PTOData | null>(null);

    const [requestData, setRequestData] = useState({
        policyId: "",
        startDate: new Date().toString(),
        endDate: new Date().toString(),
        notes: ""
    });

    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Time Off Request"}));
    }, []);

    useEffect(() => {

        let erId : string | undefined = undefined;

        console.log("[AppTimeOffRequest] dynamicContext: ", dynamicContext);

        // don't load the data if we are disabled
        if (disabled) {
            return;
        }

        // are we in a chat with dynamic context or should we assume this is a user?
        if (dynamicContext) {
            erId = dynamicContext.employeeRecordIds ? dynamicContext.employeeRecordIds[0] : undefined;
        } else {
            if (!storeUser?.employeeRecordId) {
                toast("You are not an employee", toastFail);
                return;
            }
        }

        setEmployeeRecordId(erId);
        console.log("[AppTimeOffRequest] useEffect/erId: ", erId);
    
        if (storeUser?.currentWorkspaceId && erId) {
            (async () => {
                const er = await apiGetEmployeeRecord(erId);

                if (!er) {
                    toast("Can't load employee record", toastFail);
                    console.error("[AppTimeOffRequest] Can't load employee record!!!!!!!!: ", erId);
                    return;
                }

                setEmployeeRecord(er);
                
                const thePolicies = await apiGetPaidTimeOffPoliciesForWorkspace(storeUser.currentWorkspaceId || "");

                if (!thePolicies) {
                    toast("No PTO Policies found for this workspace", toastFail);
                    return;
                }
              
                setPolicies(thePolicies);
                console.log("[AppTimeOffRequest] thePolicies: ", thePolicies);

                if (thePolicies.length > 0) {
                    setRequestData({...requestData, policyId: thePolicies[0].id });

                    const thePTOData = await getPTODataForEmployeeRecord(
                        erId || "",
                        thePolicies[0].id,
                        new Date()
                    );
    
                    setPTOData(thePTOData);
                }
            
                
            })();
        }
    }, [storeUser, dynamicContext]);

    useEffect(() => {
        if (params?.timeOffRequestId) {
            (async () => {
                const tor = await apiGetTimeOffRequest(params.timeOffRequestId!);
                if (!tor) {
                    toast("Failed to fetch time off request", toastFail);
                    return;
                }

                console.log("[AppTimeOffRequest] tor: ", tor);
                setTimeOffRequest(tor);
                setRequestData({
                    ...requestData,
                    policyId: tor.paidTimeOffPolicyId,
                    startDate: tor.startDate.toString(),
                    endDate: tor.endDate.toString(),
                    notes: tor.notes || ""
                });
            })();            
        }
    }, [params]);

    useEffect(() => {
        (async() => {
            if (requestData.policyId && storeUser?.employeeRecordId) {
                const ptoData = await getPTODataForEmployeeRecord(
                    storeUser?.employeeRecordId,
                    requestData.policyId,
                    new Date(requestData.startDate)
                )

                if (!ptoData) {
                    toast("Failed to fetch pto data", toastFail);
                    return;
                }

                setPTOData(ptoData);
            }
        })();
    }, [requestData.policyId, requestData.startDate, requestData.endDate]);

    // useEffect(() => {
    //     setHasChanges(true);
    // }, [requestData]);

    const isAfter = (d1: Date, d2: Date) => {
        const d1WithoutTime = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
        const d2WithoutTime = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
        return d1WithoutTime > d2WithoutTime;
    }; 

    const calculateTimeRequested = (start: string, end: string) => {
        if (!start || !end) return 0;

        console.log("[AppTimeOffRequest] calculateTimeRequested, start: ", start);
        console.log("[AppTimeOffRequest] calculateTimeRequested, end: ", end);

        if (isAfter(new Date(), new Date(start))) {
            toast("Start date cannot be in the past", toastFail);
            return;
        }

        if (isAfter(new Date(start), new Date(end))) {
            toast("Start date cannot be after end date", toastFail);
            return;
        }

        const startDate = new Date(start);
        const endDate = new Date(end);

        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        setTimeRequested(diffDays);
    };
    
    const handleChangePolicy = (policyId: string) => {
        setRequestData({
            ...requestData,
            policyId: policyId,
        });
    }

    const handleSubmit = async () => {
        if (!storeUser?.currentWorkspaceId || !storeUser?.employeeRecordId) {
            setError("User information is missing");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        const request: Partial<TimeOffRequest> = {
            id: "",
            employeeRecordId: storeUser.employeeRecordId,
            workspaceId: storeUser.currentWorkspaceId,
            paidTimeOffPolicyId: requestData.policyId,
            startDate: new Date(requestData.startDate),
            endDate: new Date(requestData.endDate),
            status: TimeOffRequestStatus.PENDING,
            notes: requestData.notes,
        }

        
        const response = await apiCreateTimeOffRequest(request);

        setIsSubmitting(false);

        if (!response) {
            toast("Failed to create time off request", toastFail);
            return;
        }

        if (dynamicContext?.message) {
            // update message
            const contentObject = JSON.parse(dynamicContext?.message?.content || "{}");

            const payload: UpdateMessageContentContract["Body"] = {
                messageId: dynamicContext?.message?.id,
                content: JSON.stringify({...contentObject, 
                    label: "submitted"
                })
            }
            
            const response = await apiUpdateMessageContent(payload);
        }

        onNavigation ? onNavigation?.("pto-history", "list", dynamicContext) :
        navigate("/app/time-off");
    }

    const handleCancel = async() => {
        if (dynamicContext?.message) {
            // update message
            const contentObject = JSON.parse(dynamicContext?.message?.content || "{}");

            const payload: UpdateMessageContentContract["Body"] = {
                messageId: dynamicContext?.message?.id,
                content: JSON.stringify({...contentObject, 
                    label: "cancel"
                })
            }
            
            const response = await apiUpdateMessageContent(payload);
        }

        onNavigation ? onNavigation?.("pto-request", "cancel", undefined) :
        navigate("/app/time-off"); 
    }

    // const availableBalance = ptoData ? ptoData.accrualMap.get(requestData.policyId) || 0 : 0;
    const availableBalance = 24;

    console.log("[AppTimeOffRequest] dynamicContext: ", dynamicContext);
    console.log("[AppTimeOffRequest] requestData: ", requestData);
    console.log("[AppTimeOffRequest] ptoData: ", ptoData);
    console.log("[AppTimeOffRequest] ptoData.accrualMap: ", ptoData?.accrualMap);

    const er = employeeRecord || null;
    const fullName = erGetCPValue(er, "system-firstName") + " " + erGetCPValue(er, "system-lastName")

    console.log("[AppTimeOffRequest] hasChanges: ", hasChanges);

    if (disabled) {
        return (
            <div className="app-time-off-disabled">
                Time Off Request no longer available.
            </div>
        );
    }

    const policyOptions = policies.map(policy => ({
        displayName: policy.name,
        value: policy.id,
    }));

    const selectedPolicy = policyOptions.find(option => option.value === requestData.policyId);

    return (
        <div className="app-time-off-request">
            <div className="app-time-off-request-header dc-component-header">
                Request time off
                <div className="app-time-off-request-header-actions">
                    <Icon
                        variant="tertiary"
                        size="md"
                        icon={<X size={16} />}
                        onClick={handleCancel}
                    />
                </div>
            </div>
                {!balanceOnly && 
                    <>
                        <div className="app-time-off-request-container">
                            <div className="app-time-off-request-request">

                                <div className="app-time-off-form-section">
                                    <label htmlFor="policy">Select policy</label>
                                    <GenericSelect
                                        options={policyOptions}
                                        selected={selectedPolicy}
                                        onChange={(value: string) => {
                                            handleChangePolicy(value);
                                        }}
                                    />
                                    {/* <select
                                        id="policy"
                                        value={requestData.policyId}
                                        onChange={(e) => {
                                            handleChangePolicy(e.target.value);
                                            // setRequestData({ ...requestData, policyId: e.target.value })
                                        }}
                                        required
                                    >
                                        {policies.map(policy => (
                                            <option key={policy.id} value={policy.id}>
                                                {policy.name}
                                            </option>
                                        ))}
                                    </select> */}
                                </div>

                                <div className="app-time-off-date-section">
                                    <div className="app-time-off-form-group">
                                        <label htmlFor="startDate">Start date</label>
                                        <DatePicker
                                            selected={requestData.startDate ? new Date(requestData.startDate) : null} 
                                            dateFormat={"MM/dd/yyyy"}
                                            onChange={(date) => {
                                                console.log("DatePicker start date", date);
                                                const startDate = date ? date.toISOString() : "";
                                                const endDate = !date ? requestData.endDate :
                                                    (date.getTime() > new Date(requestData.endDate).getTime()) ? date.toISOString() : requestData.endDate;

                                                setRequestData({
                                                    ...requestData,
                                                    startDate,
                                                    endDate
                                                });

                                                setHasChanges(true);
                                                calculateTimeRequested(startDate, endDate);
                                            }}
                                            className="date-picker-input"
                                            wrapperClassName="app-date-picker-wrapper"
                                        />
                                        {/* <input
                                            type="date"
                                            id="startDate"
                                            value={requestData.startDate}
                                            onChange={(e) => handleDateChange('startDate', e.target.value)}
                                            required
                                        /> */}
                                    </div>

                                    <div className="app-time-off-form-group">
                                        <label htmlFor="endDate">End date</label>
                                        <DatePicker
                                            selected={requestData.endDate ? new Date(requestData.endDate) : null} 
                                            dateFormat={"MM/dd/yyyy"}
                                            onChange={(date) => {
                                                console.log("DatePicker end date", date);
                                                const endDate = date ? date.toISOString() : "";
                                                const startDate = !date ? requestData.startDate :
                                                    (date.getTime() < new Date(requestData.startDate).getTime()) ? date.toISOString() : requestData.startDate;

                                                setRequestData({
                                                    ...requestData,
                                                    startDate,
                                                    endDate
                                                });

                                                setHasChanges(true);
                                                calculateTimeRequested(startDate, endDate);
                                            }}
                                            className="date-picker-input"
                                            wrapperClassName="app-date-picker-wrapper"
                                        />
                                        {/* <input
                                            type="date"
                                            id="endDate"
                                            value={requestData.endDate}
                                            onChange={(e) => handleDateChange('endDate', e.target.value)}
                                            required
                                        /> */}
                                    </div>
                                </div>

                                <div className="app-time-off-form-section">
                                    <label htmlFor="notes">Add comment <span className="app-time-off-optional">Optional</span></label>
                                    <p className="app-time-off-note">Your manager will receive this message</p>
                                    <textarea
                                        id="notes"
                                        value={requestData.notes}
                                        onChange={(e) => {
                                            setRequestData({ ...requestData, notes: e.target.value })
                                            setHasChanges(true);
                                        }}
                                        
                                    />
                                </div>

                                {error && <div className="app-time-off-error-message">{error}</div>}
                            </div>

                            

                            <div className="app-time-off-review-panel">
                                <h2>Review</h2>
                                <div className="app-time-off-balance-info">
                                    <div className="app-time-off-balance-row">
                                        <span>Available balance</span>
                                        <span>{availableBalance} days</span>
                                    </div>
                                    <div className="app-time-off-balance-row">
                                        <span>Time requested</span>
                                        <span>{timeRequested} days</span>
                                    </div>
                                    <div className="app-time-off-balance-row">
                                        <span>Remaining</span>
                                        <span className={availableBalance - timeRequested < 0 ? 'app-time-off-negative' : ''}>
                                            {availableBalance - timeRequested} days
                                        </span>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {hasChanges && (
                            <AppChanges
                                submitText={"Submit"}
                                onSubmit={handleSubmit}
                                onCancel={handleCancel}
                                saveDisabled={
                                    isSubmitting || 
                                    timeRequested > availableBalance || 
                                    !requestData.policyId || 
                                    isAfter(new Date(), new Date(requestData.startDate)) ||
                                    isAfter(new Date(requestData.startDate), new Date(requestData.endDate))}
                            />
                        )}

                    </>
                }

                {balanceOnly && 
                    <div className="app-time-off-review-panel">
                        <h2>Review</h2>
                        <div className="app-time-off-balance-info">
                            <div className="app-time-off-balance-row">
                                <span>Available balance</span>
                                <span>{availableBalance} days</span>
                            </div>
                            {/* <div className="app-time-off-balance-row">
                                <span>Time requested</span>
                                <span>{timeRequested} days</span>
                            </div> */}
                            {/* <div className="app-time-off-balance-row">
                                <span>Remaining</span>
                                <span className={availableBalance - timeRequested < 0 ? 'app-time-off-negative' : ''}>
                                    {availableBalance - timeRequested} days
                                </span>
                            </div> */}
                        </div>

                    </div>
                }

        </div>
    );
}; 